/**
 * `appsFromCapabilities` — the ONE reading of a capability ORDER as apps (APP-ENTITLEMENT-001 Phase 7): what a
 * company that holds `['pos', 'website', …]` is entitled to, derived from the catalog's own `capabilityKeys` rather
 * than a second table. Two readers share it: the backfill that turns every live `tenantCapabilities` row into
 * entitlement rows, and the console's tenant creation while it still takes a capability list (Phase 9 turns that
 * form into an anchor and apps).
 *
 * The rule: an app is named by a capability it grants. An anchor is named OUTRIGHT by a key only it grants (`pos`
 * → ParcelPuffin; `blog`, `owner-console`, `analytics`, `page-access` → PublishPuffin); a key the anchors share
 * (`website`, `hosting`) names PublishPuffin only when no anchor was named outright — beside ParcelPuffin it is a
 * ParcelPuffin feature. When two anchors are named outright, ParcelPuffin outranks. Every other key names exactly one
 * app. A key no app grants is reported, never dropped. The set may name no anchor (a company holding
 * only `business-development`): the caller decides, by ruling, what anchors it.
 *
 * @module @puffin-core/app-catalog/rules/capabilityMapping
 */

'use strict';

const { APP_CATALOG, ANCHOR_APP_KEYS, WEB_ANCHOR_APP_KEY } = require('../catalog');
const { APP_KIND } = require('../vocabulary');

/**
 * @typedef {Object} AppsFromCapabilities
 * @property {?string} anchorAppKey - The anchor the keys name, or null when they name none.
 * @property {string[]} appKeys - Every non-anchor app named, in catalog order (free apps included when named).
 * @property {string[]} unmapped - Keys no catalog app grants.
 */

/**
 * @param {Iterable<string>} capabilities - A company's capability keys (its order).
 * @returns {AppsFromCapabilities}
 * @throws {TypeError} When `capabilities` is not iterable.
 */
function appsFromCapabilities(capabilities) {
  if (capabilities === null || typeof capabilities?.[Symbol.iterator] !== 'function') {
    throw new TypeError('appsFromCapabilities: capabilities must be iterable');
  }
  const held = new Set(capabilities);
  const anchors = APP_CATALOG.filter((entry) => entry.kind === APP_KIND.ANCHOR);
  const apps = APP_CATALOG.filter((entry) => entry.kind !== APP_KIND.ANCHOR);
  // A key exactly ONE anchor grants, and no app, names it outright (`pos` → ParcelPuffin, `field-service` →
  // ProPuffin). A key the anchors share (the hosted website's set: `website`, `hosting`, `analytics`,
  // `owner-console`, `page-access`), or one an anchor AND an app grant (`blog`: PublishPuffin's site includes it,
  // the Auto-blog app sells it beside another anchor), names PublishPuffin only when no anchor was named outright —
  // beside ParcelPuffin it is a feature of ParcelPuffin's, or the Auto-blog app's.
  const anchorsGranting = (key) => anchors.filter((entry) => entry.capabilityKeys.includes(key));
  const appsGranting = (key) => apps.filter((entry) => entry.capabilityKeys.includes(key));
  const namedOutright = anchors.filter((entry) =>
    entry.capabilityKeys.some(
      (key) => held.has(key) && anchorsGranting(key).length === 1 && appsGranting(key).length === 0,
    ),
  );
  const namedByShared = anchors.filter((entry) =>
    entry.capabilityKeys.some(
      (key) => held.has(key) && (anchorsGranting(key).length > 1 || appsGranting(key).length > 0),
    ),
  );
  const anchorCandidates =
    namedOutright.length > 0
      ? namedOutright
      : namedByShared.filter((entry) => entry.appKey === WEB_ANCHOR_APP_KEY);
  const anchor =
    ANCHOR_APP_KEYS.map((key) => anchorCandidates.find((entry) => entry.appKey === key)).find(
      Boolean,
    ) || null;
  // An app names itself by a held key only where it can ATTACH: beside PublishPuffin a held `blog` is the anchor's
  // own feature, never the Auto-blog app.
  const appKeys = apps
    .filter(
      (entry) =>
        entry.capabilityKeys.some((key) => held.has(key)) &&
        (anchor === null || entry.attachesTo.includes(anchor.appKey)),
    )
    .map((entry) => entry.appKey);
  const granted = new Set(APP_CATALOG.flatMap((entry) => entry.capabilityKeys));
  const unmapped = [...held].filter((key) => !granted.has(key));
  return { anchorAppKey: anchor === null ? null : anchor.appKey, appKeys, unmapped };
}

module.exports = { appsFromCapabilities };
